import type { ComponentProps, VFC } from 'react';
import React from 'react';
import { useFragment } from 'react-relay';

import { ViewCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/ViewCommentShortcutListener.ts';
import { ContentOperationsFragment } from '@confluence/entity-operations/entry-points/ContentOperationsFragment.graphql.ts';
import type { ContentOperationsFragment$key } from '@confluence/entity-operations/entry-points/ContentOperationsFragmentKey.ts';
import { expVal } from '@confluence/feature-experiments';

import { getCanAddCommentsFromContentOperations } from './getCanAddCommentsFromContentOperations.ts';

type ViewPageCommentShortcutListenerProps = ComponentProps<typeof ViewCommentShortcutListener> & {
	contentOperationsFragmentRef?: ContentOperationsFragment$key | null;
};

const ViewPageCommentShortcutListenerWithFragment: VFC<
	Omit<ViewPageCommentShortcutListenerProps, 'contentOperationsFragmentRef'> & {
		contentOperationsFragmentRef: ContentOperationsFragment$key;
	}
> = ({ contentOperationsFragmentRef, ...props }) => {
	const contentOperations = useFragment(ContentOperationsFragment, contentOperationsFragmentRef);

	return (
		<ViewCommentShortcutListener
			{...props}
			preloadedCanAddComments={getCanAddCommentsFromContentOperations(contentOperations)}
		/>
	);
};

export const ViewPageCommentShortcutListener: VFC<ViewPageCommentShortcutListenerProps> = ({
	contentOperationsFragmentRef,
	...props
}) => {
	if (
		contentOperationsFragmentRef &&
		expVal('confluence_inline_comment_relay_content_ops', 'isEnabled', false)
	) {
		return (
			<ViewPageCommentShortcutListenerWithFragment
				{...props}
				contentOperationsFragmentRef={contentOperationsFragmentRef}
			/>
		);
	}

	return <ViewCommentShortcutListener {...props} />;
};
